import { useState, useEffect } from 'react';
import type { Brand, PartnerCompany, PartnerUser } from '../types';
import { getApiUrl } from '../config/api';

interface CompanyForm {
  name: string;
  contactName: string;
  email: string;
  phone: string;
  notes: string;
  brandIds: string[];
  userName: string;
  userEmail: string;
  userPassword: string;
}

const EMPTY_FORM: CompanyForm = {
  name: '',
  contactName: '',
  email: '',
  phone: '',
  notes: '',
  brandIds: [],
  userName: '',
  userEmail: '',
  userPassword: '',
};

export function CompaniesPage() {
  const [companies, setCompanies] = useState<PartnerCompany[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState<PartnerCompany | null>(null);
  const [form, setForm] = useState<CompanyForm>(EMPTY_FORM);
  const [error, setError] = useState('');

  // Add-login modal state
  const [loginCompany, setLoginCompany] = useState<PartnerCompany | null>(null);
  const [loginForm, setLoginForm] = useState({ name: '', email: '', password: '' });

  useEffect(() => {
    fetchCompanies();
    fetch(getApiUrl('/api/brands'), { credentials: 'include' })
      .then(r => r.ok ? r.json() : { brands: [] })
      .then(d => setBrands((d.brands || []).filter((b: Brand) => b.active)))
      .catch(() => {});
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await fetch(getApiUrl('/api/companies'), { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setCompanies(data.companies || []);
      }
    } catch (e) {
      console.error('Failed to fetch companies:', e);
    } finally {
      setLoading(false);
    }
  };

  const brandName = (id: string) => brands.find(b => b.id === id)?.name || id;

  const openCreate = () => {
    setEditingCompany(null);
    setForm(EMPTY_FORM);
    setError('');
    setShowModal(true);
  };

  const openEdit = (company: PartnerCompany) => {
    setEditingCompany(company);
    setForm({
      ...EMPTY_FORM,
      name: company.name,
      contactName: company.contact_name || '',
      email: company.email || '',
      phone: company.phone || '',
      notes: company.notes || '',
      brandIds: company.brand_ids,
    });
    setError('');
    setShowModal(true);
  };

  const toggleBrand = (brandId: string) => {
    setForm(prev => ({
      ...prev,
      brandIds: prev.brandIds.includes(brandId)
        ? prev.brandIds.filter(id => id !== brandId)
        : [...prev.brandIds, brandId],
    }));
  };

  const handleSave = async () => {
    setError('');

    if (!form.name.trim()) {
      setError('Company name is required');
      return;
    }
    if (form.brandIds.length === 0) {
      setError('Select at least one site to receive leads from');
      return;
    }

    const creatingLogin = !editingCompany && (form.userName || form.userEmail || form.userPassword);
    if (creatingLogin && (!form.userName || !form.userEmail)) {
      setError('To create a login, fill in at least the login name and email');
      return;
    }

    setSaving(true);
    try {
      const url = editingCompany
        ? getApiUrl(`/api/companies/${editingCompany.id}`)
        : getApiUrl('/api/companies');

      const body: any = editingCompany
        ? {
            name: form.name,
            contact_name: form.contactName || null,
            email: form.email || null,
            phone: form.phone || null,
            notes: form.notes || null,
            brandIds: form.brandIds,
          }
        : {
            name: form.name,
            contactName: form.contactName || undefined,
            email: form.email || undefined,
            phone: form.phone || undefined,
            notes: form.notes || undefined,
            brandIds: form.brandIds,
            user: creatingLogin
              ? {
                  name: form.userName,
                  email: form.userEmail,
                  password: form.userPassword || undefined,
                }
              : undefined,
          };

      const response = await fetch(url, {
        method: editingCompany ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      const data = await response.json();
      if (response.ok) {
        setShowModal(false);
        fetchCompanies();
        if (data.invited) {
          alert(`Invite email sent to ${form.userEmail} — they'll set their own password.`);
        }
      } else {
        setError(data.error || 'Failed to save company');
      }
    } catch (e) {
      setError('Failed to save company');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (company: PartnerCompany) => {
    const deactivating = !!company.active;
    if (deactivating && !confirm(`Deactivate "${company.name}"?\n\nThey will stop receiving leads and their logins will be locked out.`)) {
      return;
    }
    try {
      const response = await fetch(getApiUrl(`/api/companies/${company.id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ active: !company.active }),
      });
      if (response.ok) {
        fetchCompanies();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to update company');
      }
    } catch {
      alert('Failed to update company');
    }
  };

  const handleAddLogin = async () => {
    if (!loginCompany) return;
    setError('');

    if (!loginForm.name || !loginForm.email) {
      setError('Name and email are required');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(getApiUrl(`/api/companies/${loginCompany.id}/users`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: loginForm.name,
          email: loginForm.email,
          password: loginForm.password || undefined,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        setLoginCompany(null);
        setLoginForm({ name: '', email: '', password: '' });
        fetchCompanies();
        if (data.invited) {
          alert(`Invite email sent to ${loginForm.email} — they'll set their own password.`);
        }
      } else {
        setError(data.error || 'Failed to create login');
      }
    } catch {
      setError('Failed to create login');
    } finally {
      setSaving(false);
    }
  };

  const handleResendInvite = async (company: PartnerCompany, user: PartnerUser) => {
    try {
      const response = await fetch(getApiUrl(`/api/companies/${company.id}/users/${user.id}/invite`), {
        method: 'POST',
        credentials: 'include',
      });
      const data = await response.json();
      if (response.ok) {
        alert(data.message || 'Invite email sent');
        fetchCompanies();
      } else {
        alert(data.error || 'Failed to send invite');
      }
    } catch {
      alert('Failed to send invite');
    }
  };

  const handleRemoveLogin = async (company: PartnerCompany, user: PartnerUser) => {
    if (!confirm(`Remove login for ${user.email}?`)) return;
    try {
      const response = await fetch(getApiUrl(`/api/companies/${company.id}/users/${user.id}`), {
        method: 'DELETE',
        credentials: 'include',
      });
      if (response.ok) {
        fetchCompanies();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to remove login');
      }
    } catch {
      alert('Failed to remove login');
    }
  };

  const handleResetPassword = async (company: PartnerCompany, user: PartnerUser) => {
    const password = prompt(`New password for ${user.email} (min 8 characters):`);
    if (!password) return;
    try {
      const response = await fetch(getApiUrl(`/api/companies/${company.id}/users/${user.id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ password }),
      });
      const data = await response.json();
      if (response.ok) {
        alert('Password updated');
      } else {
        alert(data.error || 'Failed to update password');
      }
    } catch {
      alert('Failed to update password');
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1 sm:mb-2">Companies</h1>
          <p className="text-dark-text-secondary text-sm sm:text-base">
            Partner companies that receive leads from your sites
          </p>
        </div>
        <button onClick={openCreate} className="btn btn-primary">
          + Add Company
        </button>
      </div>

      {/* Companies list */}
      {loading ? (
        <div className="card p-8 text-center text-dark-text-secondary">Loading companies...</div>
      ) : companies.length === 0 ? (
        <div className="card p-8 text-center text-dark-text-secondary">
          No partner companies yet — add one to start sharing leads
        </div>
      ) : (
        <div className="space-y-4">
          {companies.map(company => (
            <div key={company.id} className={`card p-6 ${!company.active ? 'opacity-60' : ''}`}>
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap mb-2">
                    <h2 className="text-xl font-bold text-white">{company.name}</h2>
                    <span className={`px-2 py-1 rounded text-xs font-medium border ${
                      company.active
                        ? 'bg-green-900/50 text-green-400 border-green-700'
                        : 'bg-red-900/50 text-red-400 border-red-700'
                    }`}>
                      {company.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  <div className="text-sm text-dark-text-secondary space-y-1 mb-3">
                    {company.contact_name && <p>Contact: {company.contact_name}</p>}
                    {company.email && <p>Lead notifications: {company.email}</p>}
                    {company.phone && <p>Phone: {company.phone}</p>}
                  </div>

                  {/* Assigned sites */}
                  <div className="flex items-center gap-2 flex-wrap mb-3">
                    <span className="text-xs font-bold uppercase tracking-wider text-dark-text-secondary">Sites:</span>
                    {company.brand_ids.length === 0 ? (
                      <span className="text-xs text-dark-text-secondary italic">None assigned</span>
                    ) : (
                      company.brand_ids.map(id => (
                        <span key={id} className="px-2 py-1 rounded text-xs font-medium border bg-blue-900/50 text-blue-400 border-blue-700">
                          {brandName(id)}
                        </span>
                      ))
                    )}
                  </div>

                  {/* Logins */}
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold uppercase tracking-wider text-dark-text-secondary">Logins:</span>
                      <button
                        onClick={() => { setLoginCompany(company); setLoginForm({ name: '', email: '', password: '' }); setError(''); }}
                        className="text-xs text-blue-400 hover:text-blue-300"
                      >
                        + Add login
                      </button>
                    </div>
                    {company.users.length === 0 ? (
                      <p className="text-xs text-dark-text-secondary italic">No logins yet</p>
                    ) : (
                      <div className="space-y-1">
                        {company.users.map(user => (
                          <div key={user.id} className="flex items-center gap-3 text-sm flex-wrap">
                            <span className={user.status === 'active' ? 'text-dark-text' : 'text-dark-text-secondary line-through'}>
                              {user.name} · {user.email}
                            </span>
                            {user.status === 'active' && !!user.invite_pending && (
                              <span className="px-2 py-0.5 rounded text-xs font-medium border bg-yellow-900/50 text-yellow-400 border-yellow-700">
                                Invite pending
                              </span>
                            )}
                            {user.status === 'active' && !user.invite_pending && user.last_login_at && (
                              <span className="text-xs text-dark-text-secondary">
                                Last login {new Date(user.last_login_at).toLocaleDateString()}
                              </span>
                            )}
                            {user.status === 'active' && (
                              <>
                                {!!user.invite_pending && (
                                  <button
                                    onClick={() => handleResendInvite(company, user)}
                                    className="text-xs text-blue-400 hover:text-blue-300"
                                  >
                                    Resend invite
                                  </button>
                                )}
                                <button
                                  onClick={() => handleResetPassword(company, user)}
                                  className="text-xs text-blue-400 hover:text-blue-300"
                                >
                                  Reset password
                                </button>
                                <button
                                  onClick={() => handleRemoveLogin(company, user)}
                                  className="text-xs text-red-400 hover:text-red-300"
                                >
                                  Remove
                                </button>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-2 shrink-0">
                  <button onClick={() => openEdit(company)} className="btn btn-secondary text-sm">
                    Edit
                  </button>
                  <button
                    onClick={() => handleToggleActive(company)}
                    className={`btn text-sm ${company.active ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-green-600 hover:bg-green-700 text-white'}`}
                  >
                    {company.active ? 'Deactivate' : 'Reactivate'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-start sm:items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
          <div className="card p-4 sm:p-6 w-full max-w-2xl my-2 sm:my-0">
            <div className="flex items-start justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">
                {editingCompany ? 'Edit Company' : 'Add Company'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-dark-text-secondary hover:text-white">
                ✕
              </button>
            </div>

            {error && (
              <div className="bg-red-900/30 border border-red-700 rounded-lg p-3 mb-4 text-sm text-red-400">
                {error}
              </div>
            )}

            <div className="space-y-4 mb-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-dark-text mb-2">Company Name *</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    className="input w-full"
                    placeholder="Acme Copiers Ltd"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-dark-text mb-2">Contact Name</label>
                  <input
                    type="text"
                    value={form.contactName}
                    onChange={e => setForm({ ...form, contactName: e.target.value })}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-dark-text mb-2">Notification Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    className="input w-full"
                    placeholder="New leads are emailed here"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-dark-text mb-2">Phone</label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={e => setForm({ ...form, phone: e.target.value })}
                    className="input w-full"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-dark-text mb-2">Notes</label>
                <textarea
                  value={form.notes}
                  onChange={e => setForm({ ...form, notes: e.target.value })}
                  className="input w-full min-h-[80px]"
                />
              </div>

              {/* Site selection */}
              <div>
                <label className="block text-sm font-medium text-dark-text mb-2">
                  Sites they receive leads from *
                </label>
                <div className="grid sm:grid-cols-2 gap-2">
                  {brands.map(brand => (
                    <label
                      key={brand.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        form.brandIds.includes(brand.id)
                          ? 'border-blue-600 bg-blue-900/30'
                          : 'border-dark-border bg-dark-bg hover:bg-slate-700'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={form.brandIds.includes(brand.id)}
                        onChange={() => toggleBrand(brand.id)}
                        className="accent-blue-600"
                      />
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-dark-text truncate">{brand.name}</div>
                        <div className="text-xs text-dark-text-secondary truncate">{brand.domain}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Initial login (create only) */}
              {!editingCompany && (
                <div className="border-t border-dark-border pt-4">
                  <p className="text-sm font-medium text-dark-text mb-1">Dashboard Login (optional)</p>
                  <p className="text-xs text-dark-text-secondary mb-3">
                    Give the company a login so they can view their leads. They will only see lead details for their assigned sites.
                    Leave the password blank to email them a welcome invite so they set their own.
                  </p>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-dark-text mb-2">Name</label>
                      <input
                        type="text"
                        value={form.userName}
                        onChange={e => setForm({ ...form, userName: e.target.value })}
                        className="input w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-dark-text mb-2">Email</label>
                      <input
                        type="email"
                        value={form.userEmail}
                        onChange={e => setForm({ ...form, userEmail: e.target.value })}
                        className="input w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-dark-text mb-2">Password (optional)</label>
                      <input
                        type="text"
                        value={form.userPassword}
                        onChange={e => setForm({ ...form, userPassword: e.target.value })}
                        className="input w-full"
                        placeholder="Blank = send invite"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowModal(false)} className="btn btn-secondary">
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving} className="btn btn-primary">
                {saving ? 'Saving...' : editingCompany ? 'Save Changes' : 'Create Company'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Login Modal */}
      {loginCompany && (
        <div className="fixed inset-0 bg-black/50 flex items-start sm:items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
          <div className="card p-4 sm:p-6 w-full max-w-md my-2 sm:my-0">
            <div className="flex items-start justify-between mb-6">
              <h2 className="text-xl font-bold text-white">Add Login — {loginCompany.name}</h2>
              <button onClick={() => setLoginCompany(null)} className="text-dark-text-secondary hover:text-white">
                ✕
              </button>
            </div>

            {error && (
              <div className="bg-red-900/30 border border-red-700 rounded-lg p-3 mb-4 text-sm text-red-400">
                {error}
              </div>
            )}

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-dark-text mb-2">Name</label>
                <input
                  type="text"
                  value={loginForm.name}
                  onChange={e => setLoginForm({ ...loginForm, name: e.target.value })}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-text mb-2">Email</label>
                <input
                  type="email"
                  value={loginForm.email}
                  onChange={e => setLoginForm({ ...loginForm, email: e.target.value })}
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-text mb-2">Password (optional)</label>
                <input
                  type="text"
                  value={loginForm.password}
                  onChange={e => setLoginForm({ ...loginForm, password: e.target.value })}
                  className="input w-full"
                  placeholder="Leave blank to email an invite"
                />
                <p className="mt-2 text-xs text-dark-text-secondary">
                  Leave blank to send them a welcome email with a link to set their own password.
                </p>
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              <button onClick={() => setLoginCompany(null)} className="btn btn-secondary">
                Cancel
              </button>
              <button onClick={handleAddLogin} disabled={saving} className="btn btn-primary">
                {saving ? 'Creating...' : loginForm.password ? 'Create Login' : 'Send Invite'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
